/**
 * Guest funnel analytics — fire-and-forget event recording.
 *
 * Generates and persists a `cadence_guest_session_id` in localStorage so the
 * same visitor's events tie together across page reloads / device-time. POSTs
 * to /api/guest-funnel/event silently — never throws, never logs to the user.
 *
 * Valid events (must match VALID_EVENTS in guest_funnel_routes.py):
 *   breadcrumb_mounted, breadcrumb_shimmer_1, breadcrumb_shimmer_75,
 *   breadcrumb_clicked, modal_50_shown, modal_50_dismissed, modal_50_saved,
 *   modal_90_shown, modal_90_dismissed, modal_90_saved
 */

const SESSION_ID_KEY = 'cadence_guest_session_id';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ensureSessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = `gs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    // Private mode / disabled storage — fall back to a tab-lifetime id.
    return `gs_ephemeral_${Date.now()}`;
  }
};

export const getGuestSessionId = () => ensureSessionId();

export const recordGuestFunnelEvent = (eventName, progressPercent = null) => {
  if (!API_URL) return;
  try {
    const guest_session_id = ensureSessionId();
    const body = { guest_session_id, event_name: eventName };
    if (typeof progressPercent === 'number' && Number.isFinite(progressPercent)) {
      body.progress_percent = Math.round(progressPercent * 10) / 10;
    }
    // Use sendBeacon when available so the request survives navigation; fall
    // back to plain fetch with keepalive otherwise.
    const url = `${API_URL}/api/guest-funnel/event`;
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* silent */ });
  } catch {
    /* silent */
  }
};
